// Papelera de tickets y tareas — devolver lo que se borró por error.
//
// Lissette, 2026-10-15: "créame una papelera en tickets y tareas, que si se
// borran se puedan recuperar". Borrar un ticket o una tarea ya no los
// destruye: quedan marcados con la fecha de borrado y aquí se listan los de
// los últimos N días con un botón "Devolver". Es el mismo modelo que la
// papelera de clientes (ClientsTrashModal.jsx), que se queda aparte porque
// la suya arrastra pólizas y comisiones y explica cuáles vuelven.
//
// Una sola ventana para los dos: cambia el título, cómo se llama cada fila y
// las dos llamadas al servidor.
import { useEffect, useState } from 'react';
import { Trash2, RotateCcw, ShieldCheck } from 'lucide-react';
import { Modal } from './ui.jsx';
import { toast } from './Toast.jsx';
import { IS_DEMO } from '../demoMode.js';

// Fecha y hora en formato de EE. UU. y en hora LOCAL (toISOString correría el
// día al oeste de UTC).
function fmtStamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const DIAS = [30, 90, 180, 365];

/**
 * @param titulo     Título de la ventana.
 * @param nombreDe   (fila) => texto principal de la fila.
 * @param detalleDe  (fila) => texto secundario (cliente, asignado...) o ''.
 * @param listar     (dias) => Promise<filas>.
 * @param devolver   (id) => Promise<lo devuelto>.
 * @param onRestored (lo devuelto) => void, para que la pantalla de atrás lo pinte.
 */
export default function PapeleraModal({ lang = 'es', titulo, vacio, nombreDe, detalleDe, listar, devolver, onRestored, onClose }) {
  const es = lang === 'es';
  const [dias, setDias] = useState(90);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    // La demo pública no tiene servidor: una papelera vacía es la verdad allí.
    if (IS_DEMO) { setRows([]); return undefined; }
    let vivo = true;
    setRows(null); setError(null);
    listar(dias)
      .then((r) => { if (vivo) setRows(r); })
      .catch((e) => { if (vivo) setError(e.message || String(e)); });
    return () => { vivo = false; };
  }, [dias, listar]);

  const alDevolver = async (r) => {
    setBusyId(r.id);
    try {
      const res = await devolver(r.id);
      setRows((prev) => (prev || []).filter((x) => x.id !== r.id));
      toast.success(es ? `"${nombreDe(r)}" volvió.` : `"${nombreDe(r)}" restored.`);
      if (onRestored) onRestored(res);
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudo devolver.' : 'Could not restore.'));
    } finally {
      setBusyId(null);
    }
  };

  const cell = { padding: '.5rem .6rem', fontSize: '.78rem', color: 'var(--c-text,#c8dff0)', borderBottom: '1px solid var(--c-border,#1a3550)' };
  const head = { ...cell, fontSize: '.64rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.07em', fontWeight: 700 };

  return (
    <Modal title={titulo} onClose={onClose} wide>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.8rem' }}>
        <div style={{ fontSize: '.8rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.55, display: 'flex', gap: '.55rem', alignItems: 'flex-start' }}>
          <ShieldCheck size={16} style={{ color: 'var(--c-cyan,#00d4ff)', flexShrink: 0, marginTop: 2 }} />
          <span>{es
            ? 'Lo que se borra no se destruye: se guarda aquí. "Devolver" lo trae de vuelta tal como estaba, con sus notas.'
            : 'Deleted items are not destroyed: they are kept here. "Restore" brings them back as they were, with their notes.'}</span>
        </div>

        <div style={{ display: 'flex', gap: '.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', fontWeight: 700 }}>
            {es ? 'Borrados en los últimos:' : 'Deleted in the last:'}
          </span>
          {DIAS.map((d) => (
            <button key={d} type="button" onClick={() => setDias(d)}
              style={{
                padding: '.24rem .6rem', borderRadius: 100, cursor: 'pointer', fontFamily: 'inherit', fontSize: '.72rem', fontWeight: 700,
                border: `1px solid ${dias === d ? 'rgba(0,212,255,.45)' : 'var(--c-border,#1a3550)'}`,
                background: dias === d ? 'var(--c-cyan-dim,rgba(0,212,255,.12))' : 'transparent',
                color: dias === d ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text3,#45688a)',
              }}>
              {d} {es ? 'días' : 'days'}
            </button>
          ))}
        </div>

        {error && (
          <div style={{ fontSize: '.78rem', color: 'var(--c-red,#ff3d6b)', background: 'var(--c-red-dim)', border: '1px solid rgba(255,61,107,.25)', borderRadius: 7, padding: '.55rem .8rem' }}>{error}</div>
        )}
        {rows === null && !error && (
          <div style={{ fontSize: '.82rem', color: 'var(--c-text3,#45688a)', textAlign: 'center', padding: '1.5rem' }}>{es ? 'Cargando…' : 'Loading…'}</div>
        )}
        {rows && rows.length === 0 && !error && (
          <div style={{ fontSize: '.82rem', color: 'var(--c-text3,#45688a)', textAlign: 'center', padding: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '.5rem' }}>
            <Trash2 size={22} style={{ opacity: .6 }} />
            {vacio}
          </div>
        )}

        {rows && rows.length > 0 && (
          <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid var(--c-border,#1a3550)', borderRadius: 9 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...head, textAlign: 'left' }}>{es ? 'Qué' : 'What'}</th>
                  <th style={{ ...head, textAlign: 'left' }}>{es ? 'Se borró' : 'Deleted'}</th>
                  <th style={{ ...head, textAlign: 'left' }}>{es ? 'Quién' : 'Who'}</th>
                  <th style={{ ...head, textAlign: 'right' }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={cell}>
                      <div style={{ fontWeight: 600 }}>{nombreDe(r)}</div>
                      {detalleDe && detalleDe(r) && <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)' }}>{detalleDe(r)}</div>}
                    </td>
                    <td style={{ ...cell, whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', fontSize: '.72rem' }}>{fmtStamp(r.deletedAt)}</td>
                    <td style={{ ...cell, color: 'var(--c-text2,#7aa3c0)' }}>{r.deletedByName || '—'}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>
                      <button type="button" className="dkt-btn-g xs" onClick={() => alDevolver(r)} disabled={busyId === r.id} style={{ whiteSpace: 'nowrap' }}>
                        <RotateCcw size={12} /> {busyId === r.id ? (es ? 'Devolviendo…' : 'Restoring…') : (es ? 'Devolver' : 'Restore')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}
